<script setup>
import { computed } from 'vue'
import { t, schemaText } from '../lib/i18n.mjs'
import AppIcon from './AppIcon.vue'
const props = defineProps({ modelValue: Array, unitId: String, document: Object, links: Array })
const emit = defineEmits(['update:modelValue'])
const others = computed(() => props.document.units.filter((unit) => unit.id !== props.unitId))
function update(id, patch) {
  emit(
    'update:modelValue',
    props.modelValue.map((edge) => (edge.id === id ? { ...edge, ...patch } : edge)),
  )
}
function endpoint(edge) {
  return edge.source === props.unitId ? edge.target : edge.source
}
function direction(edge, outgoing) {
  const other = endpoint(edge)
  update(
    edge.id,
    outgoing ? { source: props.unitId, target: other } : { source: other, target: props.unitId },
  )
}
function add() {
  emit('update:modelValue', [
    ...props.modelValue,
    {
      id: `R_${crypto.randomUUID()}`,
      level: 'unit',
      source: props.unitId,
      target: '',
      type: props.links[0].id,
      text: '',
      note: '',
      joint: '',
    },
  ])
}
function label(unit) {
  return `${unit.paragraphId} · ${unit.id} · ${unit.display_label || unit.sentenceIds.join(', ')}`
}
</script>
<template>
  <section class="unit-relations-editor" :aria-label="t('单元关系')">
    <header>
      <h3>
        {{ t('单元关系') }} <span>{{ modelValue.length }}</span>
      </h3>
      <button type="button" class="secondary-button" :disabled="!links.length || !others.length" @click="add">
        <AppIcon name="Plus" :size="13" />{{ t('增加关系') }}
      </button>
    </header>
    <p class="relation-help">{{ t('可连接同段或其他段落的论证单元；所有修改在保存标注后生效。') }}</p>
    <p v-if="!modelValue.length" class="relation-help">{{ t('暂无关联关系。') }}</p>
    <fieldset
      v-for="(edge, index) in modelValue"
      :key="edge.id"
      class="unit-relation-row"
      :data-relation-id="edge.id"
    >
      <legend>{{ t('关系') }} {{ index + 1 }}</legend>
      <div class="unit-relation-fields">
        <label
          >{{ t('方向') }}
          <select
            :aria-label="t('方向')"
            :value="edge.source === unitId ? 'out' : 'in'"
            @change="direction(edge, $event.target.value === 'out')"
          >
            <option value="out">{{ t('当前单元 → 对方') }}</option>
            <option value="in">{{ t('对方 → 当前单元') }}</option>
          </select>
        </label>
        <label
          >{{ t('关联单元') }}
          <select
            :aria-label="t('关联单元')"
            :value="endpoint(edge)"
            required
            @change="update(edge.id, { [edge.source === unitId ? 'target' : 'source']: $event.target.value })"
          >
            <option disabled value="">{{ t('选择论证单元') }}</option>
            <option v-for="unit in others" :key="unit.id" :value="unit.id">{{ label(unit) }}</option>
          </select>
        </label>
        <label
          >{{ t('关系类型') }}
          <select
            :aria-label="t('关系类型')"
            :value="edge.type"
            @change="
              update(edge.id, { type: links.find((type) => String(type.id) === $event.target.value).id })
            "
          >
            <option v-for="type in links" :key="type.id" :value="type.id">
              {{ schemaText(type, 'name', 'link') }}
            </option>
          </select>
        </label>
      </div>
      <div class="unit-relation-actions">
        <details>
          <summary>{{ t('关系说明与备注') }}</summary>
          <label
            >{{ t('关系说明')
            }}<textarea :value="edge.text" rows="2" @input="update(edge.id, { text: $event.target.value })" />
          </label>
          <label
            >{{ t('标注备注')
            }}<textarea :value="edge.note" rows="2" @input="update(edge.id, { note: $event.target.value })" />
          </label>
          <label
            >{{ t('联合支撑组 ID')
            }}<input :value="edge.joint" @input="update(edge.id, { joint: $event.target.value })"
          /></label>
        </details>
        <button
          type="button"
          class="danger-button"
          @click="
            emit(
              'update:modelValue',
              modelValue.filter((item) => item.id !== edge.id),
            )
          "
        >
          <AppIcon name="Trash2" :size="13" />{{ t('删除关系') }}
        </button>
      </div>
    </fieldset>
  </section>
</template>
